import React, { useCallback, useEffect, useState } from "react";
import { FolderNavigation, NoDataView, NoteInfo } from "../../../components";
import { deepClone } from "../../../utils";
import { getAccHubsAPI, getAccProjectsAPI, getAccContentsAPI } from "../../../common/services";

/**
 * Browse Autodesk Construction Cloud with the shared FolderNavigation file
 * viewer (hub → project → folders → document files), fed by the docs ACC REST
 * endpoints (fileType="docs", so only documents show). Browsing + importing both
 * go through /docs/acc/*, so item ids line up. Files and folders are both
 * selectable; a selected folder imports everything under it. Emits the current
 * selection upward as the import payload ({ hubId, projectId, items, folders }).
 */
const AccFileBrowser = ({ onSelectionChange }) => {
    const [hubs, setHubs] = useState([]);
    const [selectedHub, setSelectedHub] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [navData, setNavData] = useState([]);
    const [breadCrumbs, setBreadCrumbs] = useState([]);
    // picked nav items; files also carry `folderName`, the folder they were picked in
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isFirstStep = breadCrumbs.length === 0;
    // breadcrumbs run project → folders, so the last one is where we are
    const currentCrumb = breadCrumbs[breadCrumbs.length - 1];
    const currentFolder = currentCrumb?.type === NODE_TYPE.FOLDER ? currentCrumb : null;

    // Load a project's (or sub-folder's) contents and stitch them into the tree.
    const loadContents = useCallback(async (hub, project, folder) => {
        if (!hub || !project) return;
        setLoading(true);
        setError("");
        try {
            const res = await getAccContentsAPI({
                hubId: hub.id,
                projectId: project.id,
                folderId: folder?.id,
                fileType: "docs",
            });
            const contents = (res?.data?.items || []).map(parseAccNavItem);
            setNavData((old) => {
                const next = deepClone(old);
                traverseAndUpdate(next, folder?.id || project.id, contents);
                return next;
            });
        } catch {
            setError("Couldn't load folder contents.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Pick a hub → load its projects as the top navigation level.
    const selectHub = useCallback(async (hub) => {
        setLoading(true);
        setError("");
        try {
            const res = await getAccProjectsAPI(hub.id);
            setSelectedHub(hub);
            setSelectedProject(null);
            setSelectedItems([]);
            setNavData((res?.data?.items || []).map(parseAccNavItem));
            setBreadCrumbs([]);
        } catch {
            setError("Couldn't load projects.");
        } finally {
            setLoading(false);
        }
    }, []);

    const handleNavItemClick = useCallback((item) => {
        if (item.type === NODE_TYPE.PROJECT) {
            setSelectedProject(item);
            setSelectedItems([]);
            loadContents(selectedHub, item);
        } else if (item.type === NODE_TYPE.FOLDER) {
            loadContents(selectedHub, selectedProject, item);
        }
    }, [selectedHub, selectedProject, loadContents]);

    // Toggle a set of listed items; "Select all" passes everything listed (after search).
    const toggleItems = useCallback((items, checked) => {
        const ids = new Set(items.map((item) => item.id));
        const picked = items.map((item) => ({ ...item, folderName: currentFolder?.displayName || "" }));
        setSelectedItems((prev) => {
            const rest = prev.filter((item) => !ids.has(item.id));
            return checked ? [...rest, ...picked] : rest;
        });
    }, [currentFolder]);

    const handleSelect = useCallback((item) => {
        toggleItems([item], !selectedItems.some((selectedItem) => selectedItem.id === item.id));
    }, [toggleItems, selectedItems]);

    // Hubs on mount; auto-select when there's only one. Keep the loader up
    // continuously through the auto-selected project's load so the empty-state
    // never flashes between the hubs and projects requests.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getAccHubsAPI()
            .then((res) => {
                if (cancelled) return;
                const list = res?.data?.items || [];
                setHubs(list);
                if (list.length === 1) {
                    // selectHub keeps `loading` true until the projects arrive.
                    selectHub(list[0]);
                } else {
                    setLoading(false);
                }
            })
            .catch(() => {
                if (cancelled) return;
                setError("Couldn't load Autodesk hubs. Make sure you're signed in to Autodesk.");
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectHub]);

    // Surface the current selection to the parent.
    useEffect(() => {
        const folders = selectedItems.filter((item) => item.type === NODE_TYPE.FOLDER);
        const files = selectedItems.filter((item) => item.type !== NODE_TYPE.FOLDER);
        onSelectionChange && onSelectionChange({
            hubId: selectedHub?.id || "",
            projectId: selectedProject?.id || "",
            items: files.map((file) => ({ itemId: file.id, displayName: file.displayName, folderName: file.folderName })),
            folders: folders.map((folder) => ({ folderId: folder.id, displayName: folder.displayName })),
        });
    }, [selectedHub, selectedProject, selectedItems, onSelectionChange]);

    return (
        <div className="create-acc-project-container acc-browser">
            <NoteInfo withInfoIcon theme="dark" note="You can select PDF, Word and Excel file types only" />
            {error && <p className="acc-browser-error">{error}</p>}

            <FolderNavigation
                items={navData}
                isLoading={loading}
                onClick={handleNavItemClick}
                onSelect={handleSelect}
                multiple
                onSelectAll={toggleItems}
                className="acc-file-selection"
                selected={selectedItems.map((item) => item.id)}
                withSearch
                breadCrumbs={breadCrumbs}
                onBreadCrumbChange={setBreadCrumbs}
                onBackButtonClick={() => setBreadCrumbs([])}
                noDataText="No project found"
                emptyContent={<EmptyFolderContent />}
            >
                <div className="hubs-container">
                    {isFirstStep && hubs.length > 1 && (
                        <div className="hubs">
                            {hubs.map((hub) => (
                                <div
                                    key={hub.id}
                                    className={`item ${selectedHub?.id === hub.id ? "selected" : ""}`}
                                    onClick={() => selectHub(hub)}
                                >
                                    {accName(hub)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </FolderNavigation>
        </div>
    );
};

const EmptyFolderContent = () => (
    <div className="empty-folder-container">
        <p className="title">Folder is not found</p>
        <p className="description">The folder you are looking for does not exist or has been moved.</p>
        <NoDataView noDataText="Nothing here" size="medium" />
    </div>
);

const accName = (node) =>
    node?.attributes?.displayName || node?.attributes?.name || node?.name || node?.id || "";

const NODE_TYPE = { PROJECT: "projects", FOLDER: "folders" };

const parseAccNavItem = (node) => {
    const parsed = {
        ...node,
        id: node.id,
        displayName: accName(node),
        navigable: node.type === NODE_TYPE.FOLDER || node.type === NODE_TYPE.PROJECT,
        // projects are only browsed; folders import everything under them
        selectable: node.type !== NODE_TYPE.PROJECT,
    };
    if (node.children) parsed.children = node.children.map(parseAccNavItem);
    return parsed;
};

const traverseAndUpdate = (items, id, newItems) => {
    items.forEach((item) => {
        if (item.id === id) {
            item.children = newItems;
            return;
        }
        if (item.children) traverseAndUpdate(item.children, id, newItems);
    });
};

export default AccFileBrowser;
